import { FC } from 'react'

import styled from 'styled-components'

import { variables } from 'src/Styled/variables'
import { addSpaceToNumber } from 'src/Helpers/layoutHelpers'

import { Avatar } from '../Avatar'

const ArtistName = styled.p`
  font-family: 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 200px;
`

const FollowersContainer = styled.div`
  display: flex;
  gap: 4px;
  font-family: 'Segoe UI', sans-serif;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
`

const AvatarWrapper = styled.div``

const Container = styled.div<{
  disabled?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  border-radius: 16px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  ${({ disabled, theme }) =>
    disabled &&
    `
    ${ArtistName} {
      color: ${theme.color.lightGrey};
    }

    ${FollowersContainer} {
      color: ${theme.color.lightGrey};
    }
  `}

  &:hover {
    background-color: ${({ theme, disabled }) =>
      disabled ? 'transparent' : theme.color.hoverWhite};
  }

  &:active {
    ${ArtistName} {
      color: ${({ theme, disabled }) =>
        disabled ? theme.color.lightGrey : theme.color.slateGrey};
    }

    ${FollowersContainer} {
      color: ${({ theme, disabled }) =>
        disabled ? theme.color.lightGrey : theme.color.slateGrey};
    }

    ${AvatarWrapper} {
      filter: grayscale(0.5);
      opacity: 0.5;
    }
  }

  &:focus-visible {
    outline: ${({ theme, disabled }) =>
      disabled ? 'none' : `1px solid ${theme.color.darkGreen}`};
    background-color: ${({ theme, disabled }) =>
      disabled ? 'transparent' : theme.color.hoverWhite};
    outline-offset: -1px;
  }
`

const DISABLED_TAB_INDEX = -1
const ENABLED_TAB_INDEX = 0

export type ArtistItemData = {
  artistName?: string
  avatarSrc?: string
  followers?: number
  onClick?: () => void
  disabled?: boolean
  artistSpotifyId?: string
}

export const ArtistItem: FC<ArtistItemData> = ({
  artistName,
  avatarSrc,
  followers,
  onClick,
  disabled,
}) => {
  const onClickInternal = (): void => {
    if (onClick || (onClick && !disabled)) {
      onClick()
    }
  }

  const formattedFollowers = followers ? addSpaceToNumber(followers, ',') : '-'

  return (
    <Container
      onClick={onClickInternal}
      tabIndex={disabled ? DISABLED_TAB_INDEX : ENABLED_TAB_INDEX}
      disabled={disabled}
    >
      <AvatarWrapper>
        <Avatar src={avatarSrc} width={48} height={48} />
      </AvatarWrapper>
      <InfoContainer>
        <ArtistName>{artistName}</ArtistName>
        <FollowersContainer>
          {`${formattedFollowers} followers`}
        </FollowersContainer>
      </InfoContainer>
    </Container>
  )
}
